import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { FiChevronDown, FiX, FiCheck } from 'react-icons/fi';
import classNames from 'classnames';
import styles from './PillDropdown.module.scss';

/**
 * `isMulti` turns this into a checklist: `value`/`onChange` switch from a
 * single scalar to an array of `{ value, label }` options (same shape
 * react-select's multi-mode uses, so callers migrating off
 * MultiSelectDropdown don't need to reshape their filter state). The pill
 * itself never grows or wraps — it always shows one line (placeholder, the
 * one selected label, or "N selected"), and the checklist panel is where
 * individual picks actually live, instead of every selection being spelled
 * out as an inline chip.
 */
export const PillDropdown = ({
  icon,
  label,
  value,
  placeholder = 'Select',
  options = [],
  onChange,
  loading = false,
  disabled = false,
  footerAction,
  bare = false,
  isMulti = false,
  getPopupContainer,
}) => {
  const [open, setOpen] = useState(false);
  const selectedValues = isMulti ? value || [] : [];
  const selectedOption = !isMulti
    ? options.find((o) => o.value === value)
    : null;

  const isOptionSelected = (opt) =>
    isMulti
      ? selectedValues.some((v) => (v?.value ?? v) === opt.value)
      : opt.value === value;

  const handleOptionClick = (opt) => {
    if (isMulti) {
      const exists = isOptionSelected(opt);
      onChange?.(
        exists
          ? selectedValues.filter((v) => (v?.value ?? v) !== opt.value)
          : [...selectedValues, opt]
      );
      return; // keep the checklist open so more than one option can be picked
    }
    onChange?.(opt.value);
    setOpen(false);
  };

  const collapsedValue = () => {
    if (loading) return 'Loading…';
    if (isMulti) {
      if (!selectedValues.length) return placeholder;
      if (selectedValues.length === 1) {
        return selectedValues[0]?.label ?? placeholder;
      }
      return `${selectedValues.length} selected`;
    }
    return selectedOption?.label || placeholder;
  };

  const dropdownContent = (
    <div className={styles.dropdownMenu}>
      {options.length === 0 ? (
        <div className={styles.dropdownEmpty}>
          {loading ? 'Loading…' : 'No options available'}
        </div>
      ) : (
        options.map((opt) => {
          const active = isOptionSelected(opt);
          return (
            <button
              key={opt.value}
              type="button"
              className={classNames(styles.dropdownItem, {
                [styles.dropdownItemActive]: !isMulti && active,
              })}
              onClick={() => handleOptionClick(opt)}
            >
              {isMulti ? (
                <span className={styles.checklistRow}>
                  <span
                    className={classNames(styles.checklistBox, {
                      [styles.checklistBoxChecked]: active,
                    })}
                  >
                    {active && <FiCheck size={11} />}
                  </span>
                  <span className={styles.checklistLabel}>{opt.label}</span>
                </span>
              ) : (
                opt.label
              )}
            </button>
          );
        })
      )}
      {footerAction && (
        <>
          <div className={styles.dropdownDivider} />
          <button
            type="button"
            className={styles.dropdownItem}
            onClick={() => {
              footerAction.onClick?.();
              setOpen(false);
            }}
          >
            {footerAction.label}
          </button>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      disabled={disabled}
      popupRender={() => dropdownContent}
      getPopupContainer={getPopupContainer}
    >
      <button
        type="button"
        className={classNames(styles.pill, { [styles.pillBare]: bare })}
        disabled={disabled}
      >
        <span className={styles.pillIcon}>{icon}</span>
        <span className={styles.textStack}>
          <span className={styles.label}>{label}</span>
          <span className={styles.value}>{collapsedValue()}</span>
        </span>
        {isMulti && selectedValues.length > 0 && (
          <span
            className={styles.clearAll}
            role="button"
            aria-label={`Clear ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              onChange?.([]);
            }}
          >
            <FiX size={13} />
          </span>
        )}
        <FiChevronDown
          className={classNames(styles.chevron, {
            [styles.chevronOpen]: open,
          })}
        />
      </button>
    </Dropdown>
  );
};
